/**
 * Web "What we checked" body: the twin of `HealthCheckedAreas.tsx`. One row
 * per audience (Recruiters, Hiring systems, AI tools) with its status
 * ("Looks good", "2 improvements", "Couldn't check") and a chevron into the
 * area's tab, then the quiet scope line ("Based on 18 checks of your CV's
 * text and links. Not a hiring prediction."). Back from that tab hands focus
 * to the row that opened it.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CvHealthArea } from '@tabbio/types';
import { CV_HEALTH_AREA_ORDER } from '@/lib/cv-health-model';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { getAreaLabelKey, getAreaStatusRef, translateRef } from './cv-health-copy';
import { noteAreaOpened, takeAreaReturn } from './health-disclosure-memory';
import type { HealthCheckedAreasProps } from './health-props';

export type { HealthCheckedAreasProps };

export function HealthCheckedAreas({ report, onOpenArea }: HealthCheckedAreasProps) {
  const { t } = useTranslation('common');
  const rowRefs = useRef<Partial<Record<CvHealthArea, HTMLButtonElement | null>>>({});

  // Back from an area's tab remounts this overview: focus goes back to its
  // row, scrolled into view (the rows usually sit below the fold).
  useEffect(() => {
    const area = takeAreaReturn(report.id);
    const row = area ? rowRefs.current[area] : null;
    if (!row) return;
    row.focus({ preventScroll: true });
    row.scrollIntoView({ block: 'nearest' });
  }, [report.id]);

  return (
    <>
      <div className="tabbio-health-area-links">
        {CV_HEALTH_AREA_ORDER.map((area) => {
          const label = t(getAreaLabelKey(area));
          const status = translateRef(t, getAreaStatusRef(report, area));
          return (
            <button
              key={area}
              ref={(node) => {
                rowRefs.current[area] = node;
              }}
              type="button"
              className="tabbio-health-area-link"
              data-testid={`cv-health-area-${area}`}
              aria-label={`${label}, ${status}`}
              onClick={() => {
                noteAreaOpened(report.id, area);
                onOpenArea(area);
              }}
            >
              <span>{label}</span>
              <span className="tabbio-health-area-link-status">
                {status}
                <MaterialSymbol name="chevron_right" size={16} className="tabbio-rtl-flip" />
              </span>
            </button>
          );
        })}
      </div>
      {report.eligibleCount > 0 ? (
        <p className="tabbio-health-small">
          {t('cvHealth.report.basedOn', { count: report.eligibleCount })}
        </p>
      ) : null}
    </>
  );
}

export default HealthCheckedAreas;
